import Cal, { getCalApi } from '@calcom/embed-react'
import {
  faArrowLeft,
  faMicrophoneLines,
  faMoon,
  faMusic,
  faSun,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useMemo, useState } from 'react'
import ReactGA from 'react-ga4'
import { sessions } from '../../data/sessions'
import { useTheme } from '../../theme/useTheme'
import type { TimeClassConfig } from '../../types/sessions'
import { SessionTypeCard } from './components/SessionTypeCard'
import { TimeSlotButton } from './components/TimeSlotButton'

// The renderer React component.
export default function SessionSelector() {
  // Let's retrieve the variables that were chosen by
  // the user upon dropping the page element onto the page.
  const { theme } = useTheme()

  const vars = sessions[0].variables
  const [selectedKey, setSelectedKey] = useState<
    | 'weekday'
    | 'weeknight'
    | 'weekendNight'
    | 'weekendDay'
    | 'rehearsalWeeknight'
    | 'rehearsalWeekday'
    | null
  >(null)
  const [step, setStep] = useState<1 | 2>(1)
  const [sessionType, setSessionType] = useState<
    'recording' | 'rehearsal' | null
  >(null)

  // Backward compatibility: if new configs are missing, synthesise them from old flat values
  const normalized = useMemo(() => {
    const fallback: TimeClassConfig = {
      type: vars.type ?? 'inline',
      layout: vars.layout ?? 'month_view',
      calInstanceUrl: vars.calInstanceUrl ?? 'https://booking.strangeways.co',
      calendarPath: vars.calendarPath ?? 'studio/15min',
    }
    return {
      weekday: vars.weekday ?? fallback,
      weeknight: vars.weeknight ?? fallback,
      weekendNight: vars.weekendNight ?? fallback,
      weekendDay: vars.weekendDay ?? fallback,
      rehearsalWeeknight: vars.rehearsalWeeknight ?? fallback,
      rehearsalWeekday: vars.rehearsalWeekday ?? fallback,
    }
  }, [vars])

  const currentConfig: TimeClassConfig | null = selectedKey
    ? normalized[selectedKey]
    : null
  const layout = currentConfig?.layout ?? 'month_view'
  const calInstanceUrl =
    currentConfig?.calInstanceUrl ?? 'https://booking.strangeways.co'
  const calendarPath = currentConfig?.calendarPath ?? 'studio/15min'

  const namespace = useMemo(() => {
    if (!calendarPath) return 'booking'
    const parts = calendarPath.split('/')
    return parts[parts.length - 1] || 'booking'
  }, [calendarPath])

  const embedJsUrl = `${calInstanceUrl}/embed/embed.js`

  // Tailwind classes for the container
  // const containerClasses = 'container-responsive cv-auto py-16 sm:py-24'

  useEffect(() => {
    if (!selectedKey || !namespace) return

    getCalApi({
      namespace,
      embedJsUrl,
    })
      .then((cal) => {
        cal('ui', {
          cssVarsPerTheme: {
            light: { 'cal-brand': '#1f2b35' },
            dark: { 'cal-brand': '#fafafa' },
          },
          hideEventTypeDetails: false,
          layout,
          theme: theme,
        })
      })
      .catch(() => {
        // Fail silently if Cal API is not available
      })
  }, [selectedKey, namespace, embedJsUrl, layout, theme])

  // If no selection yet, show a 2-step selection UI
  if (!selectedKey) {
    // resetAll kept for potential future use
    const _resetAll = () => {
      setStep(1)
      setSessionType(null)
    }
    void _resetAll

    const chooseSessionType = (type: 'recording' | 'rehearsal') => {
      setSessionType(type)
      setStep(2)
    }

    const chooseRecordingSlot = (
      key: 'weekday' | 'weeknight' | 'weekendDay' | 'weekendNight'
    ) => {
      setSelectedKey(key)
    }

    const chooseRehearsalSlot = (
      key: 'rehearsalWeekday' | 'rehearsalWeeknight'
    ) => {
      setSelectedKey(key)
    }

    return (
      <section
        id="booking"
        className="container-responsive cv-auto py-16 sm:py-24"
      >
        <div className="mb-8 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
            Book a session
          </h2>
          <p className="text-base mt-3 leading-relaxed text-neutral-600 dark:text-neutral-400">
            Start by choosing what kind of session you are looking for, then
            pick a time slot. We will open the booking calendar so you can
            choose an exact time and confirm your deposit.
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          {/* Step indicator */}
          <div className="border-b border-neutral-100 bg-neutral-50 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-800/50">
            <div className="flex items-center gap-3">
              <div
                className={`text-sm flex h-7 w-7 items-center justify-center rounded-full font-semibold ${
                  step >= 1
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400'
                    : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'
                }`}
              >
                1
              </div>
              <div
                className={`h-0.5 w-12 rounded-full ${
                  step >= 2
                    ? 'bg-emerald-200 dark:bg-emerald-800'
                    : 'bg-neutral-200 dark:bg-neutral-700'
                }`}
              />
              <div
                className={`text-sm flex h-7 w-7 items-center justify-center rounded-full font-semibold ${
                  step >= 2
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400'
                    : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400'
                }`}
              >
                2
              </div>
              <span className="text-sm ml-2 font-medium text-neutral-600 dark:text-neutral-400">
                {step === 1 ? 'Choose session type' : 'Select time slot'}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Step 1: Session type */}
            {step === 1 && (
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                  What kind of session would you like to book?
                </h3>
                <p className="text-sm mt-1 text-neutral-600 dark:text-neutral-400">
                  Choose between a full recording session or using the room for
                  band rehearsal.
                </p>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <SessionTypeCard
                    title="Recording Session"
                    description="Full-band recording with an engineer for the duration of your booking."
                    icon={faMicrophoneLines}
                    onClick={() => {
                      chooseSessionType('recording')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Recording Session',
                        label: 'Session Selector',
                      })
                    }}
                    variant="default"
                  />
                  <SessionTypeCard
                    title="Rehearsal"
                    description="Use the sound stage as a rehearsal room with setup assistance."
                    icon={faMusic}
                    onClick={() => {
                      chooseSessionType('rehearsal')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Rehearsal',
                        label: 'Session Selector',
                      })
                    }}
                    variant="secondary"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Time slot selection - Recording */}
            {step === 2 && sessionType === 'recording' && (
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                  When would you like to record?
                </h3>
                <p className="text-sm mt-1 text-neutral-600 dark:text-neutral-400">
                  Pick the slot that best matches your schedule.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <TimeSlotButton
                    onClick={() => {
                      chooseRecordingSlot('weekday')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weekday',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faSun}
                    variant="day"
                  >
                    Weekday day
                  </TimeSlotButton>
                  <TimeSlotButton
                    onClick={() => {
                      chooseRecordingSlot('weeknight')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weeknight',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faMoon}
                    variant="night"
                  >
                    Weeknight
                  </TimeSlotButton>
                  <TimeSlotButton
                    onClick={() => {
                      chooseRecordingSlot('weekendDay')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weekend day',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faSun}
                    variant="day"
                  >
                    Weekend day
                  </TimeSlotButton>
                  <TimeSlotButton
                    onClick={() => {
                      chooseRecordingSlot('weekendNight')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weekend night',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faMoon}
                    variant="night"
                  >
                    Weekend night
                  </TimeSlotButton>
                </div>
              </div>
            )}

            {/* Step 2: Time slot selection - Rehearsal */}
            {step === 2 && sessionType === 'rehearsal' && (
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                  When would you like to rehearse?
                </h3>
                <p className="text-sm mt-1 text-neutral-600 dark:text-neutral-400">
                  Pick a rehearsal slot that works for your schedule.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <TimeSlotButton
                    onClick={() => {
                      chooseRehearsalSlot('rehearsalWeekday')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weekday daytime',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faSun}
                    variant="day"
                  >
                    Weekday daytime
                  </TimeSlotButton>
                  <TimeSlotButton
                    onClick={() => {
                      chooseRehearsalSlot('rehearsalWeeknight')
                      ReactGA.event({
                        category: 'Button Click',
                        action: 'User selected Weeknight evening',
                        label: 'Session Selector',
                      })
                    }}
                    icon={faMoon}
                    variant="night"
                  >
                    Weeknight evening
                  </TimeSlotButton>
                </div>
              </div>
            )}

            {/* Back button for step 2 */}
            {step === 2 && (
              <button
                onClick={() => {
                  setStep(1)
                  ReactGA.event({
                    category: 'Button Click',
                    action: 'User returned to step 1',
                    label: 'Session Selector',
                  })
                }}
                className="text-sm mt-6 inline-flex cursor-pointer items-center gap-1.5 font-medium text-neutral-500 transition-colors hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to session type
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-100 bg-neutral-50 px-6 py-3 dark:border-neutral-800 dark:bg-neutral-800/30">
            <p className="text-xs text-neutral-500 dark:text-neutral-500">
              Bookings require a non-refundable 50% deposit. Promo rates apply
              to sessions scheduled before March 31, 2026.
            </p>
          </div>
        </div>
      </section>
    )
  }

  // Always render a single inline Cal embed once a time class has been selected
  if (!currentConfig) {
    return null
  }

  return (
    <section
      id="booking"
      className="container-responsive cv-auto py-16 sm:py-24"
    >
      <div className="mb-8 max-w-2xl">
        <h2 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">
          Book a session
        </h2>
        <p className="text-base mt-3 leading-relaxed text-neutral-600 dark:text-neutral-400">
          Choose an available time slot below to complete your booking.
        </p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-800/50">
          <div className="flex items-center gap-2">
            <div className="text-sm flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
              ✓
            </div>
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Session selected
            </span>
          </div>
          <button
            className="text-sm inline-flex cursor-pointer items-center gap-1.5 font-medium text-neutral-500 transition-colors hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
            onClick={() => {
              setSelectedKey(null)
              ReactGA.event({
                category: 'Button Click',
                action: 'User clicked to change selection',
                label: 'Session Selector',
              })
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Change selection
          </button>
        </div>
        <div className="p-4">
          <Cal
            namespace={namespace}
            calLink={calendarPath}
            style={{
              width: '100%',
              height: '100%',
              minHeight: '550px',
              overflow: 'scroll',
            }}
            config={{ layout }}
            calOrigin={calInstanceUrl}
            embedJsUrl={embedJsUrl}
          />
        </div>
      </div>
    </section>
  )
}
